import React from 'react';
import { AppIdea } from '@/hooks/app-library';
import { TextContentSidebarContext } from '@/components/text-content-lightbox';
import { AppIdeaNavigationButtons, AppIdeaParametersSection, AppIdeaActionButtons } from './sidebar';
import { Skeleton } from '@/components/ui/skeleton';

// Sidebar Loading Skeleton Component (matching our flattened structure)
const SidebarLoadingSkeleton: React.FC = () => {
  return (
    <div className="flex flex-col h-full space-y-3">
      {/* Navigation Buttons Skeleton */}
      <div className="rounded-2xl bg-background/80 p-3 border border-accent/20">
        <div className="flex gap-2">
          <Skeleton className="h-10 flex-1" />
          <Skeleton className="h-10 flex-1" />
        </div>
      </div>

      {/* Flattened Parameters Section Skeleton */}
      <div className="flex-1 space-y-3">
        {/* App Details Skeleton */}
        <div className="p-3 bg-background/50 dark:bg-background/80 rounded-2xl border">
          <div className="flex items-start gap-3">
            <Skeleton className="h-4 w-4 rounded mt-0.5" />
            <div className="flex-1">
              <Skeleton className="h-4 w-20 mb-2" />
              <div className="space-y-1">
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-5/6" />
              </div>
            </div>
          </div>
        </div>

        {/* Research Section Skeleton */}
        <div className="p-3 bg-background/50 dark:bg-background/80 rounded-2xl border">
          <div className="flex items-start gap-3">
            <Skeleton className="h-4 w-4 rounded mt-0.5" />
            <div className="flex-1">
              <Skeleton className="h-4 w-32 mb-2" />
              <div className="space-y-1">
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-4/5" />
                <Skeleton className="h-3 w-5/6" />
              </div>
            </div>
          </div>
        </div>

        {/* Related Questions Skeleton */}
        <div className="p-3 bg-background/50 dark:bg-background/80 rounded-2xl border">
          <div className="flex items-start gap-3">
            <Skeleton className="h-4 w-4 rounded mt-0.5" />
            <div className="flex-1">
              <Skeleton className="h-4 w-28 mb-2" />
              <div className="space-y-1">
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-5/6" />
                <Skeleton className="h-3 w-4/5" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons Skeleton */}
      <div className="rounded-2xl bg-background/80 p-3 border border-accent/20">
        <div className="flex flex-col gap-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      </div>
    </div>
  );
};

interface AppIdeaSidebarRendererProps {
  idea: AppIdea;
  context: TextContentSidebarContext<AppIdea>;
  onCloseCurrentLightbox?: () => void;
  onDelete?: (idea: AppIdea) => Promise<void>;
  onDownload?: (idea: AppIdea) => void;
}

export const AppIdeaSidebarRenderer: React.FC<AppIdeaSidebarRendererProps> = ({ 
  idea, 
  context, 
  onCloseCurrentLightbox,
  onDelete,
  onDownload
}) => {

  // Show loading skeleton when content is loading
  if (context.isLoading) {
    return <SidebarLoadingSkeleton />;
  }
  
  return (
    <div className="flex flex-col h-full space-y-3">
      {/* Navigation Buttons */}
      <AppIdeaNavigationButtons {...context} />
      
      {/* Parameters and Content - NO scroll container */}
      <div className="flex-1 space-y-3">
        {/* Details and Research Section with cross-lightbox navigation */}
        <AppIdeaParametersSection 
          idea={idea} 
          onCloseCurrentLightbox={onCloseCurrentLightbox}
        />
      </div>
      
      {/* Action Buttons */}
      <div className="rounded-2xl bg-background/80 p-3 border border-accent/20">
        <AppIdeaActionButtons 
          idea={idea}
          onDelete={onDelete}
          onDownload={onDownload}
        />
      </div>
    </div>
  );
};
